import { useEffect, useRef } from 'react';

type ActivityType = 'update' | 'logout';

interface UseActivityTrackerOptions {
  sessionId: string;
  checkIntervalMs?: number;
  onSendActivity: (params: {
    sessionId: string;
    type: ActivityType;
  }) => Promise<unknown>;
}

const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000;

export function useActivityTracker({
  sessionId,
  onSendActivity,
  checkIntervalMs = DEFAULT_CHECK_INTERVAL,
}: UseActivityTrackerOptions) {
  const wasActiveRef = useRef(false);

  useEffect(() => {
    const handleActivity = () => {
      wasActiveRef.current = true;
    };

    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'click',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ];

    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    const intervalId = window.setInterval(async () => {
      const type: ActivityType = wasActiveRef.current
        ? 'update'
        : 'logout';

      wasActiveRef.current = false;

      try {
        if (type === 'update') {
          await onSendActivity({
            sessionId,
            type,
          });
        }
      } catch (error) {
        console.error('Failed to send activity', error);
      }
    }, checkIntervalMs);

    return () => {
      clearInterval(intervalId);

      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [sessionId, onSendActivity, checkIntervalMs]);
}

export default useActivityTracker;
